import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="popover"
export default class extends Controller {
  static targets = ['card', 'content']
  static values = {
    url: String,
  }

  disconnect() {
    this._cancelHide()
    this._hideCard()
  }

  async show(event) {
    this._overTrigger = true
    this._cancelHide()
    this._hideCard()
    const element = event.currentTarget
    let content = null;

    if (this.hasContentTarget) {
      content = this.contentTarget.innerHTML
    } else {
      content = await this.fetch()
    }

    if (!content) return

    const fragment = document.createRange().createContextualFragment(content)
    const card = fragment.querySelector('[data-popover-target="card"]')

    if (card) {
      // Use fixed positioning appended to body to escape overflow-hidden containers
      card.style.position = 'fixed'

      card.addEventListener('mouseenter', () => {
        this._overCard = true
        this._cancelHide()
      })
      card.addEventListener('mouseleave', () => {
        this._overCard = false
        this._scheduleHide()
      })

      this._bodyCard = card
      document.body.appendChild(fragment)

      // Neutralize the Tailwind `-translate-x-1/2` (it centers the inline/relative
      // fallback) so our explicit fixed coordinates aren't shifted left by half the
      // card width — that's what pushed wide tooltips off the left edge.
      card.style.transform = 'none'

      // Position relative to the trigger, clamped inside the viewport so it never
      // spills off-screen (flip above the trigger if it would overflow the bottom).
      const rect = element.getBoundingClientRect()
      const margin = 8
      const cardWidth = card.offsetWidth
      const cardHeight = card.offsetHeight

      let left = rect.left + rect.width / 2 - cardWidth / 2
      left = Math.max(margin, Math.min(left, window.innerWidth - cardWidth - margin))

      let top = rect.bottom + 4
      if (top + cardHeight > window.innerHeight - margin && rect.top - cardHeight - 4 >= margin) {
        top = rect.top - cardHeight - 4
      }

      card.style.left = `${left}px`
      card.style.top = `${top}px`
    } else {
      element.appendChild(fragment)
    }
  }

  hide() {
    this._overTrigger = false
    this._scheduleHide()
  }

  _hideCard() {
    if (this._bodyCard) {
      this._bodyCard.remove()
      this._bodyCard = null
    }
    if (this.hasCardTarget) {
      this.cardTarget.remove()
    }
    this._overCard = false
  }

  _scheduleHide() {
    this._cancelHide()
    this._hideTimeout = setTimeout(() => {
      if (!this._overTrigger && !this._overCard) {
        this._hideCard()
      }
    }, 150)
  }

  _cancelHide() {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout)
      this._hideTimeout = null
    }
  }

  async fetch() {
    if (!this.remoteContent) {
      if (!this.hasUrlValue) {
        console.error('[stimulus-popover] You need to pass an url to fetch the popover content.')
        return
      }

      const response = await fetch(this.urlValue)
      this.remoteContent = await response.text()
    }

    return this.remoteContent
  }
}